/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, EventEmitter, Inject, Input, NgZone, OnInit, Optional, Output, signal} from '@angular/core';
import {ControlContainer} from '@angular/forms';
import * as _ from 'lodash';

import template from './file-upload.component.html';
import type {FileUploadHandler, FileUploadWarningOptions, UploadableFile} from './file-upload.util';
import {
  buildFileLimitExceededWarning,
  buildUploadWarnings,
  toUploadableFiles
} from './file-upload.util';
import {setFormValidity} from '../../controller/form-validity';
import {APP_SHARED} from '../../app-shared';

export interface FileUploadState {
  pristine: boolean;
  pending: boolean;
  invalid: boolean;
}

@Component({
  standalone: true,
  imports: [...APP_SHARED],
  selector: 'adn-file-upload',
  template
})
export class FileUploadComponent implements OnInit {
  @Input() name = 'upload';
  @Input() accept = '';
  @Input() fileUploadLimit = 1;
  @Input() dropTranslationKey = 'button.upload.asset.drop';
  @Input() fileLimitExceededSingular = false;
  @Input() validateForm = true;
  @Input() warningOptions: FileUploadWarningOptions = {mode: 'reference-data'};
  @Input() successWarningsPath = '';
  @Input() uploadFile: FileUploadHandler;

  @Output() onUpdate = new EventEmitter<any>();
  @Output() onStateChange = new EventEmitter<FileUploadState>();

  private readonly allFilesProcessedState = signal(true);
  private readonly allFilesProgressState = signal(0);
  private readonly filesFailedUploadState = signal<UploadableFile[]>([]);
  private readonly isDragOverState = signal(false);
  private readonly validationWarningsState = signal<any[]>([]);

  get allFilesProcessed(): boolean { return this.allFilesProcessedState(); }
  set allFilesProcessed(value: boolean) { this.allFilesProcessedState.set(value); }
  get allFilesProgress(): number { return this.allFilesProgressState(); }
  set allFilesProgress(value: number) { this.allFilesProgressState.set(value); }
  get filesFailedUpload(): UploadableFile[] { return this.filesFailedUploadState(); }
  set filesFailedUpload(value: UploadableFile[]) { this.filesFailedUploadState.set(value); }
  get isDragOver(): boolean { return this.isDragOverState(); }
  set isDragOver(value: boolean) { this.isDragOverState.set(value); }
  get validationWarnings(): any[] { return this.validationWarningsState(); }
  set validationWarnings(value: any[]) { this.validationWarningsState.set(value); }

  constructor(
    @Optional() @Inject(ControlContainer) private readonly parentForm: ControlContainer,
    private readonly ngZone: NgZone
  ) {
  }

  ngOnInit(): void {
    this.emitState(true, false, false);
  }

  openFilePicker(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.upload(toUploadableFiles(input.files));
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    this.upload(toUploadableFiles(event.dataTransfer?.files));
  }

  trackByFileName(index: number, file: UploadableFile): string {
    return `${file.name}-${index}`;
  }

  private upload(allFiles: UploadableFile[]): void {
    if (!allFiles.length || !this.uploadFile) {
      if (this.validateForm && !allFiles.length) {
        this.setUploadValidity(false);
      }
      this.emitState(false, false, true);
      return;
    }

    const files = allFiles.slice(0, this.fileUploadLimit);

    this.filesFailedUpload = [];
    this.updateWarnings();

    if (allFiles.length > this.fileUploadLimit) {
      this.updateWarnings(buildFileLimitExceededWarning(
        this.fileUploadLimit,
        allFiles.length,
        this.fileLimitExceededSingular
      ));
    }

    this.setUploadValidity(undefined);
    this.allFilesProcessed = false;
    this.allFilesProgress = 0;
    this.emitState(false, true, false);

    _.forEach(files, (file) => {
      file.processed = false;
      file.validity = undefined;

      this.uploadFile(file, (progressEvent: any) => {
        this.ngZone.run(() => {
          file.progress = Math.min(100, parseInt(String(100 * progressEvent.loaded / progressEvent.total), 10));
          this.setAllFilesProgress(files);
        });
      }).then((result: any) => {
        this.ngZone.run(() => {
          file.processed = true;
          file.validity = true;
          this.finishUpload(files);
          this.onUpdate.emit(result);

          if (this.successWarningsPath) {
            const validationWarnings = _.get(result, this.successWarningsPath.split('.'));
            if (validationWarnings) {
              this.updateWarnings(validationWarnings);
            }
          }
        });
      }, (error: any) => {
        this.ngZone.run(() => {
          file.processed = true;
          file.validity = false;
          this.updateWarnings(buildUploadWarnings(error, this.warningOptions));
          this.finishUpload(files);
        });
      });
    });
  }

  private finishUpload(files: UploadableFile[]): void {
    if (!_.find(files, ['processed', false])) {
      this.allFilesProcessed = true;
    }

    const isValid = _.some(files, 'validity');
    if (this.validateForm) {
      this.setUploadValidity(isValid);
    }
    this.filesFailedUpload = _.filter(files, ['validity', false]);
    this.emitState(false, !this.allFilesProcessed, !isValid);
  }

  private setAllFilesProgress(files: UploadableFile[]): void {
    const progress = _.reduce(files, (result, file) => {
      return result + (_.isFinite(file.progress) ? file.progress : 0);
    }, 0);
    this.allFilesProgress = Math.min(100, parseInt(String(progress / files.length), 10));
  }

  private updateWarnings(paramWarnings?: any): void {
    if (paramWarnings) {
      this.validationWarnings = this.validationWarnings.concat(_.isArray(paramWarnings) ? paramWarnings : [paramWarnings]);
      return;
    }

    this.validationWarnings = [];
  }

  private setUploadValidity(isValid?: boolean): void {
    if (this.validateForm) {
      setFormValidity(this.parentForm, 'upload', isValid);
    }
  }

  private emitState(pristine: boolean, pending: boolean, invalid: boolean): void {
    this.onStateChange.emit({pristine, pending, invalid});
  }
}
